// src/pages/TakeTestPage/index.js
// 仕様まとめ:
// - 残り3分以下: カウントダウンが赤く点滅
// - 時間切れ: 入力無効化 + 自動採点（即時に結果画面へ） + Firestore保存は非同期で実施
// - 自動採点は「setTimeout」「Countdown.onFinish」「remainMs監視」の三重保険
// - 採点直前にフォーム値を取り込んで取りこぼし防止
// - Firestore: answers は [{ exerciseId, rows: [...] }, ...]（入れ子配列を避ける）
// - 結果画面: 完成 / 受験回数 / 問題数 / 正解数 / 不正解数 / 獲得点 / 未獲得点 を表示
// - 送信後は writeBatch.commit() により MyPage 側 onSnapshot へリアルタイム反映

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Spin,
  Card,
  Button,
  Row,
  Col,
  Statistic,
  Alert,
  Progress,
  Result,
  App,
  Table,
  Form,
} from 'antd';
import {
  ClockCircleOutlined,
  EditOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { db } from '../../firebase-config';
import {
  doc,
  getDoc,
  writeBatch,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { getExerciseDetailsById } from '../../services/exerciseService';
import { useAuth } from '../../context/AuthContext';
import QuestionRenderer from '../../components/QuestionRenderer';

const { Title, Text, Paragraph } = Typography;
const { Countdown } = Statistic;

/* ================== ヘルパー ================== */

// 未回答プレビュー/仕訳プレビュー（借方/貸方）
const AnswerPreview = ({ answer }) => {
  if (
    !answer ||
    answer.length === 0 ||
    !answer.some((a) => a && (a.debitAccount || a.creditAccount))
  ) {
    return <Text type="danger">未回答</Text>;
  }
  return (
    <div style={{ fontSize: 12, lineHeight: 1.4 }}>
      {answer.map(
        (row, idx) =>
          row && (
            <div key={idx}>
              {row.debitAccount && (
                <span>
                  借方: {row.debitAccount} -{' '}
                  {new Intl.NumberFormat().format(Number(row.debitAmount || 0))}
                </span>
              )}
              {row.creditAccount && (
                <span style={{ marginLeft: 8 }}>
                  貸方: {row.creditAccount} -{' '}
                  {new Intl.NumberFormat().format(Number(row.creditAmount || 0))}
                </span>
              )}
            </div>
          )
      )}
    </div>
  );
};

// 設問本文（テキスト + ルビ）をフル表示
const renderFullContent = (parts) => {
  if (!Array.isArray(parts)) return <Paragraph>本文がありません。</Paragraph>;
  return (
    <Paragraph style={{ fontSize: 16, lineHeight: 1.8 }}>
      {parts.map((part, index) =>
        part.type === 'ruby' ? (
          <ruby key={index}>
            {part.text}
            <rt>{part.ruby}</rt>
          </ruby>
        ) : (
          <span key={index}>{part.value}</span>
        )
      )}
    </Paragraph>
  );
};

// 回答正誤判定（借方/貸方ごとの合計金額で一致判定）
const isAnswerCorrect = (userAnswer, correctAnswer) => {
  if (!userAnswer || !correctAnswer) return false;

  const buildMap = (entries) => {
    const map = new Map();
    (entries || []).forEach((e) => {
      if (e && e.debitAccount && e.debitAmount != null) {
        const cur = map.get(e.debitAccount) || { d: 0, c: 0 };
        map.set(e.debitAccount, { ...cur, d: cur.d + Number(e.debitAmount) });
      }
      if (e && e.creditAccount && e.creditAmount != null) {
        const cur = map.get(e.creditAccount) || { d: 0, c: 0 };
        map.set(e.creditAccount, { ...cur, c: cur.c + Number(e.creditAmount) });
      }
    });
    return map;
  };

  const userMap = buildMap(userAnswer);
  const correctMap = buildMap(correctAnswer);

  if (userMap.size === 0 && correctMap.size === 0) return true;
  if (userMap.size !== correctMap.size) return false;

  for (const [acc, val] of correctMap) {
    if (!userMap.has(acc)) return false;
    const u = userMap.get(acc);
    if (u.d !== val.d || u.c !== val.c) return false;
  }
  return true;
};

// 1行の仕訳データを安全化（undefined→null, 数値化）
const sanitizeRow = (row) => ({
  debitAccount: row?.debitAccount ?? null,
  debitAmount:
    row?.debitAmount != null && row.debitAmount !== ''
      ? Number(row.debitAmount)
      : null,
  creditAccount: row?.creditAccount ?? null,
  creditAmount:
    row?.creditAmount != null && row.creditAmount !== ''
      ? Number(row.creditAmount)
      : null,
  memo: row?.memo ?? null,
});

// [ [rows], [rows], ... ] -> [{ exerciseId, rows: [...] }, ...]（Firestore入れ子配列回避）
const buildAnswersPayload = (userAnswers, questions) => {
  return (userAnswers || []).map((a, i) => ({
    exerciseId: questions?.[i]?.exerciseId ?? null,
    rows: Array.isArray(a) ? a.filter(Boolean).map(sanitizeRow) : [],
  }));
};

/* ================== コンポーネント ================== */

const TakeTestPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { message } = App.useApp();
  const [form] = Form.useForm();

  // 画面状態
  const [viewMode, setViewMode] = useState('answering'); // answering | reviewing | finished
  const [testData, setTestData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [finalScore, setFinalScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [returnToReview, setReturnToReview] = useState(false);
  const [saving, setSaving] = useState(false);

  // 結果表示用統計
  const [finalStats, setFinalStats] = useState({
    totalQuestions: 0,
    correctCount: 0,
    incorrectCount: 0,
  });

  // 受験回数（既存値）＆表示用
  const [existingAttempts, setExistingAttempts] = useState(0);
  const [attemptsDisplay, setAttemptsDisplay] = useState(null);

  // 多重実行防止
  const hasSubmittedRef = useRef(false);

  // 締切（ミリ秒）
  const deadlineTs = useMemo(() => {
    if (testData?.isTimed) {
      const minutes = Number(testData.timeLimit || 0);
      return Date.now() + minutes * 60 * 1000;
    }
    return null;
  }, [testData]);

  // 残り時間
  const [remainMs, setRemainMs] = useState(null);
  const dangerThresholdMs = 3 * 60 * 1000; // 3分
  const isDanger =
    testData?.isTimed && remainMs != null && remainMs > 0 && remainMs <= dangerThresholdMs;
  const isTimeUp = testData?.isTimed && remainMs != null && remainMs <= 0;

  /* ---------- 既存の受験回数取得 ---------- */
  useEffect(() => {
    const loadAttempts = async () => {
      try {
        const uid = currentUser?.uid || currentUser?.id;
        if (!uid || !testId) return;
        const resultId = `${uid}_${testId}`;
        const snap = await getDoc(doc(db, 'testResults', resultId));
        if (snap.exists()) {
          const data = snap.data();
          setExistingAttempts(Number(data?.attempts || 0));
        } else {
          setExistingAttempts(0);
        }
      } catch (e) {
        console.error('受験回数の取得に失敗:', e);
      }
    };
    loadAttempts();
  }, [currentUser, testId]);

  /* ---------- 採点計算 ---------- */
  const computeResults = useCallback(
    (answersArray) => {
      let score = 0;
      const individualResults = (questions || []).map((question, index) => {
        const userAnswer = answersArray[index];
        const correctAnswer = question?.details?.questions?.[0]?.answer;
        const ok = isAnswerCorrect(userAnswer, correctAnswer);
        if (ok) score += Number(question.points || 0);

        const chapterId =
          question?.details?.chapterId ??
          question?.details?.meta?.chapterId ??
          null;

        return {
          exerciseId: question.exerciseId,
          isCorrect: ok,
          levelId: testData?.levelId ?? null,
          chapterId,
        };
      });

      const totalQuestions = questions.length;
      const correctCount = individualResults.filter((r) => r.isCorrect).length;
      const incorrectCount = Math.max(0, totalQuestions - correctCount);

      return { score, individualResults, stats: { totalQuestions, correctCount, incorrectCount } };
    },
    [questions, testData]
  );

  /* ---------- Firestore 保存（非同期） ---------- */
  const saveResultsAsync = useCallback(
    async (answersArray, score, individualResults) => {
      try {
        const uid = currentUser?.uid || currentUser?.id;
        if (!uid) throw new Error('ログイン情報が無効です。');

        const answersPayload = buildAnswersPayload(answersArray, questions);
        const batch = writeBatch(db);

        // 集計ドキュメント（attempts をインクリメント）
        const resultId = `${uid}_${testId}`;
        const resultRef = doc(db, 'testResults', resultId);
        batch.set(
          resultRef,
          {
            userId: uid,
            testId,
            testName: testData?.testName || '',
            score,
            totalScore: Number(testData?.totalScore || 0),
            submittedAt: serverTimestamp(),
            answers: answersPayload,
            attempts: increment(1),
          },
          { merge: true }
        );

        // 進捗
        individualResults.forEach((res) => {
          if (!res.exerciseId) return;
          const progressId = `${uid}_${res.exerciseId}`;
          const progressRef = doc(db, 'progress', progressId);

          const data = {
            attempts: increment(1),
            completedCount: increment(res.isCorrect ? 1 : 0),
            lastAttempted: serverTimestamp(),
            status: res.isCorrect ? 'completed' : 'incomplete',
            userId: uid,
            exerciseId: res.exerciseId,
          };
          if (res.levelId != null) data.levelId = res.levelId;
          if (res.chapterId != null) data.chapterId = res.chapterId;

          batch.set(progressRef, data, { merge: true });
        });

        await batch.commit();
      } catch (e) {
        console.error('結果保存エラー:', e);
        message.error(`結果の保存に失敗しました: ${e?.message || '不明なエラー'}`);
      } finally {
        setSaving(false);
      }
    },
    [currentUser, questions, testId, testData, message]
  );

  /* ---------- 回答登録/画面遷移 ---------- */
  const handleAnswerSubmit = (values) => {
    if (isTimeUp) return; // 時間切れ後は何もしない

    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = values.entries || [];
    setUserAnswers(newAnswers);

    if (returnToReview) {
      setViewMode('reviewing');
      setReturnToReview(false);
    } else if (currentQuestionIndex < questions.length - 1) {
      form.resetFields();
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      setViewMode('reviewing');
    }
  };

  const handleGoToQuestion = (index) => {
    if (isTimeUp) return; // 時間切れなら移動も禁止
    setCurrentQuestionIndex(index);
    form.setFieldsValue({ entries: userAnswers[index] || [] });
    setReturnToReview(true);
    setViewMode('answering');
  };

  /* ---------- 採点・表示・保存（統一処理） ---------- */
  const finalizeAndSave = useCallback(
    (isAuto = false) => {
      if (hasSubmittedRef.current) return; // 二重防止
      hasSubmittedRef.current = true;

      // フォームの編集中値を取り込み
      let answersSnapshot = [...userAnswers];
      try {
        const v = form?.getFieldsValue?.();
        if (v && Array.isArray(v.entries)) {
          answersSnapshot[currentQuestionIndex] = v.entries;
        }
      } catch (_) {
        // 取得失敗時は既存 userAnswers を使用
      }

      // 採点
      const { score, individualResults, stats } = computeResults(answersSnapshot);

      // 表示用の受験回数（既存 attempts + 1）を即時更新
      setAttemptsDisplay(Number(existingAttempts || 0) + 1);

      // 結果画面へ
      setFinalScore(score);
      setFinalStats(stats);
      setViewMode('finished');

      // 保存は非同期
      setSaving(true);
      saveResultsAsync(answersSnapshot, score, individualResults);
    },
    [userAnswers, currentQuestionIndex, form, computeResults, saveResultsAsync, existingAttempts]
  );

  /* ---------- テストデータ読込 ---------- */
  useEffect(() => {
    const fetchTestData = async () => {
      setLoading(true);
      try {
        const testDocRef = doc(db, 'tests', testId);
        const testSnap = await getDoc(testDocRef);
        if (!testSnap.exists()) {
          message.error('テストが見つかりません。');
          navigate('/exams');
          return;
        }

        const data = testSnap.data();
        setTestData(data);

        const questionDetails = (data.questions || []).map((q) => ({
          ...q,
          details: getExerciseDetailsById(q.exerciseId),
        }));

        setQuestions(questionDetails);
        setUserAnswers(Array(questionDetails.length).fill(null));
      } catch (err) {
        console.error('テスト読み込みエラー:', err);
        message.error('テストの読み込みに失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchTestData();
  }, [testId, message, navigate]);

  /* ---------- 時間管理（残り3分で赤点滅、0で自動採点＆入力無効化） ---------- */

  // 残り時間のポーリング更新（0.5秒ごと）
  useEffect(() => {
    if (!testData?.isTimed || !deadlineTs) {
      setRemainMs(null);
      return;
    }
    const timer = setInterval(() => {
      const diff = (deadlineTs ?? 0) - Date.now();
      setRemainMs(diff);
    }, 500);
    setRemainMs((deadlineTs ?? 0) - Date.now()); // 初期反映
    return () => clearInterval(timer);
  }, [testData, deadlineTs]);

  // 時間切れ監視（どの画面でも確実に採点）
  useEffect(() => {
    if (!testData?.isTimed) return;
    if (remainMs != null && remainMs <= 0) {
      finalizeAndSave(true);
    }
  }, [remainMs, testData, finalizeAndSave]);

  // 正確な時刻での自動採点（保険の setTimeout）
  useEffect(() => {
    if (!testData?.isTimed || !deadlineTs) return;
    const ms = Math.max(0, deadlineTs - Date.now());
    const timeoutId = window.setTimeout(() => {
      finalizeAndSave(true);
    }, ms + 50);
    return () => window.clearTimeout(timeoutId);
  }, [testData, deadlineTs, finalizeAndSave]);

  /* ================== UI表示 ================== */

  if (loading)
    return (
      <Spin
        size="large"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      />
    );

  if (!testData)
    return (
      <Alert
        message="テストを読み込めませんでした。"
        type="error"
        showIcon
      />
    );

  // 共通CSS（点滅/オーバーレイ）
  const CommonStyles = () => (
    <style>{`
      @keyframes blink { 50% { opacity: 0.2; } }
      .countdown-blink { animation: blink 1s step-start 0s infinite; }
      .timeup-overlay {
        position: absolute; inset: 0;
        background: rgba(255,255,255,0.7);
        display: flex; align-items: center; justify-content: center;
        font-weight: 700; font-size: 16px; color: #a8071a; z-index: 5;
      }
    `}</style>
  );

  if (viewMode === 'finished') {
    const totalScore = Number(testData.totalScore || 0);
    const gotScore = Number(finalScore || 0);
    const missingScore = Math.max(0, totalScore - gotScore);
    const attemptsShown = attemptsDisplay ?? (existingAttempts + 1);

    return (
      <div>
        <CommonStyles />
        <Result
          status="success"
          title="完成"
          subTitle={`あなたの得点：${gotScore} / ${totalScore}`}
          extra={[
            <Button type="primary" key="back" onClick={() => navigate('/exams')}>
              一覧へ戻る
            </Button>,
          ]}
        />
        <Card style={{ maxWidth: 820, margin: '0 auto' }}>
          <Row gutter={[16, 16]} justify="center">
            <Col xs={12} md={8}>
              <Statistic title="問題数" value={finalStats.totalQuestions} suffix="問" />
            </Col>
            <Col xs={12} md={8}>
              <Statistic title="正解数" value={finalStats.correctCount} suffix="問" />
            </Col>
            <Col xs={12} md={8}>
              <Statistic title="不正解数" value={finalStats.incorrectCount} suffix="問" />
            </Col>
            <Col xs={12} md={8}>
              <Statistic title="獲得点" value={gotScore} suffix={` / ${totalScore} 点`} />
            </Col>
            <Col xs={12} md={8}>
              <Statistic title="未獲得点" value={missingScore} suffix="点" />
            </Col>
            <Col xs={12} md={8}>
              <Statistic title="受験回数" value={attemptsShown} suffix="回" />
            </Col>
          </Row>
        </Card>
      </div>
    );
  }

  if (viewMode === 'reviewing') {
    const reviewColumns = [
      {
        title: '設問',
        dataIndex: 'index',
        key: 'index',
        width: 60,
        render: (text) => `設問 ${text}`,
      },
      {
        title: 'あなたの回答',
        dataIndex: 'answer',
        key: 'answer',
        render: (answer) => <AnswerPreview answer={answer} />,
      },
      {
        title: '操作',
        key: 'action',
        width: 120,
        align: 'center',
        render: (_, record) => (
          <Button
            icon={<EditOutlined />}
            onClick={() => handleGoToQuestion(record.index - 1)}
            disabled={isTimeUp}
          >
            修正する
          </Button>
        ),
      },
    ];

    const reviewDataSource = (questions || []).map((q, index) => ({
      key: q.exerciseId || `q-${index}`,
      index: index + 1,
      content: q?.details?.questions?.[0]?.content || {},
      answer: userAnswers[index],
    }));

    return (
      <div style={{ position: 'relative' }}>
        <CommonStyles />
        {testData.isTimed && deadlineTs && (
          <div style={{ marginBottom: 12 }} className={isDanger ? 'countdown-blink' : ''}>
            <Countdown
              title={
                <>
                  <ClockCircleOutlined /> 残り時間
                </>
              }
              value={deadlineTs}
              onFinish={() => finalizeAndSave(true)}
              format="mm:ss"
              valueStyle={isDanger ? { color: '#ff4d4f' } : undefined}
            />
          </div>
        )}

        {isTimeUp && <div className="timeup-overlay">時間切れ：解答は無効になりました</div>}

        <Title level={2}>{testData.testName} - 回答の見直し</Title>
        <Table
          columns={reviewColumns}
          dataSource={reviewDataSource}
          bordered
          pagination={false}
          expandable={{
            expandedRowRender: (record) =>
              renderFullContent(record?.content?.parts),
            rowExpandable: (record) => !!record?.content?.parts,
          }}
        />
        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            loading={saving}
            onClick={() => finalizeAndSave(false)}
            disabled={isTimeUp}
          >
            回答を提出して採点
          </Button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion =
    currentQuestionIndex === (questions?.length || 0) - 1;

  return (
    <div>
      <CommonStyles />

      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              {testData.testName}
            </Title>
            <Progress
              percent={
                questions?.length
                  ? Math.round((currentQuestionIndex / questions.length) * 100)
                  : 0
              }
              status="active"
            />
          </Col>
          <Col>
            {testData.isTimed && deadlineTs && (
              <div className={isDanger ? 'countdown-blink' : ''}>
                <Countdown
                  title={
                    <>
                      <ClockCircleOutlined /> 残り時間
                    </>
                  }
                  value={deadlineTs}
                  onFinish={() => finalizeAndSave(true)}
                  format="mm:ss"
                  valueStyle={isDanger ? { color: '#ff4d4f' } : undefined}
                />
              </div>
            )}
          </Col>
        </Row>
      </Card>

      {currentQuestion ? (
        <Card
          title={`設問 ${currentQuestionIndex + 1}/${questions.length}：${
            currentQuestion?.details?.title || ''
          }（${currentQuestion.points} 点）`}
        >
          {/* 時間切れ時は入力を無効化 */}
          <div style={{ position: 'relative' }}>
            {isTimeUp && (
              <div className="timeup-overlay">時間切れ：解答は無効になりました</div>
            )}
            <div style={isTimeUp ? { pointerEvents: 'none', opacity: 0.6 } : undefined}>
              <QuestionRenderer
                mode="test"
                form={form}
                key={currentQuestion.exerciseId || `q-${currentQuestionIndex}`}
                question={currentQuestion?.details?.questions?.[0]}
                exerciseId={currentQuestion.exerciseId}
                onAnswerSubmit={handleAnswerSubmit}
                isLastQuestion={isLastQuestion}
                disabled={isTimeUp}
              />
            </div>
          </div>
        </Card>
      ) : (
        <Text>設問を読み込み中...</Text>
      )}
    </div>
  );
};

export default TakeTestPage;
